import React, { useState } from "react";
import api from "../api";

const ReturnByOrder = () => {
  const [orderId, setOrderId] = useState("");
  const [mobile, setMobile] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Return Modal State (same as MyOrders)
  const [showModal, setShowModal] = useState(false);
  const [maxQty, setMaxQty] = useState(1);
  const [returnData, setReturnData] = useState({
    orderItemId: "",
    quantity: 1,
    reason: "",
    comments: "",
    desired_action: "",
    photos: [],
    exchange: { sku: "", name: "", size: "" },
  });

  const fetchOrder = async () => {
    setLoading(true);
    setErrorMsg("");
    setOrder(null);

    try {
      const res = await api.post("/api/order/find", {
        order_id: orderId,
        mobile,
      });

      setOrder(res.data.order);
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || "Something went wrong");
    }

    setLoading(false);
  };

  const formatMoney = (v) =>
    Number(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const address = order ? JSON.parse(order.address) : null;

  // Open Return Modal
  const handleReturnClick = (item) => {
    setReturnData({
      orderItemId: item.id,
      quantity: 1,
      reason: "",
      comments: "",
      desired_action: "",
      photos: [],
      exchange: { sku: "", name: "", size: "" },
    });
    setMaxQty(Number(item.quantity || 1));
    setShowModal(true);
  };

  // Modal Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setReturnData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setReturnData((prev) => ({ ...prev, photos: e.target.files }));
  };

  const handleExchangeChange = (e) => {
    const { name, value } = e.target;
    setReturnData((prev) => ({
      ...prev,
      exchange: { ...prev.exchange, [name]: value },
    }));
  };

  const closeModal = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fd = new FormData();
    fd.append("order_detail_id", returnData.orderItemId);
    fd.append("quantity", returnData.quantity);
    fd.append("reason", returnData.reason);
    fd.append("desired_action", returnData.desired_action);

    if (returnData.reason === "damaged") {
      fd.append("comments", returnData.comments || "");
      Array.from(returnData.photos || []).forEach((f) =>
        fd.append("photos[]", f)
      );
    }

    if (returnData.desired_action === "exchange") {
      fd.append("exchange_sku", returnData.exchange.sku);
      fd.append("exchange_name", returnData.exchange.name);
      fd.append("exchange_size", returnData.exchange.size);
    }

    try {
      await api.get("/sanctum/csrf-cookie");
      await api.post(`/api/orders/${returnData.orderItemId}/return`, fd);

      // Update order UI just like MyOrders
      setOrder((oldOrder) => {
        const updatedItems = oldOrder.items.map((it) =>
          it.id === returnData.orderItemId
            ? { ...it, return_request_id: it.return_request_id ?? 1 }
            : it
        );
        return { ...oldOrder, items: updatedItems };
      });

      alert("Return request submitted successfully!");
      setShowModal(false);
    } catch (err) {
      console.error("Return request failed:", err);
      alert("Failed to submit return request.");
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h2 className="mb-4">Find Your Order</h2>

          <div className="card p-4 shadow">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Order ID</label>
                <input
                  className="form-control"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Mobile Number</label>
                <input
                  className="form-control"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="Enter mobile used while ordering"
                />
              </div>
            </div>

            <button
              className="btn btn-primary mt-3"
              onClick={fetchOrder}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search Order"}
            </button>

            {errorMsg && (
              <div className="alert alert-danger mt-3">{errorMsg}</div>
            )}
          </div>
        </div>
      </div>

      {/* Order Detail Box */}
      {order && (
        <div className="card mt-4 shadow-lg">
          <div className="card-header bg-light">
            <h4>Order #{order.order_id}</h4>
          </div>

          <div className="card-body">
            <h5>Customer Information</h5>
            <p>
              <strong>
                {address.first_name} {address.last_name}
              </strong>
            </p>
            <p>
              <strong>Mobile:</strong> {address.mobile}
            </p>
            <p>
              <strong>Address:</strong> {address.address}, {address.city}
            </p>

            <hr />

            <h5>Products</h5>

            <table className="table table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Image</th>
                  <th>Product</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Total</th>
                  <th>Return</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      {it.image ? <img src={it.image} width={70} /> : "N/A"}
                    </td>
                    <td>{it.product_name}</td>
                    <td>{it.color}</td>
                    <td>{it.size}</td>
                    <td>${formatMoney(it.price)}</td>
                    <td>{it.quantity}</td>
                    <td>${formatMoney(it.price * it.quantity)}</td>
                    <td>
                      {it.return_request_id ? (
                        <span className="badge bg-success">
                          Return Submitted
                        </span>
                      ) : (
                        <button
                          onClick={() => handleReturnClick(it)}
                          className="btn btn-danger btn-sm"
                        >
                          Request Return
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5>Request Return</h5>
                <button className="btn-close" onClick={closeModal}></button>
              </div>

              <div className="modal-body">
                {/* Quantity */}
                <div className="mb-3">
                  <label className="form-label">Quantity</label>
                  <select
                    name="quantity"
                    className="form-control"
                    value={returnData.quantity}
                    onChange={handleChange}
                  >
                    {Array.from({ length: maxQty }, (_, i) => i + 1).map(
                      (n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* Reason */}
                <div className="mb-3">
                  <label className="form-label">Reason</label>
                  <select
                    name="reason"
                    className="form-control"
                    value={returnData.reason}
                    onChange={handleChange}
                  >
                    <option value="">Select reason</option>
                    <option value="wrong_item">Wrong Item</option>
                    <option value="improper_fit">Improper Fit</option>
                    <option value="damaged">Damaged</option>
                    <option value="not_as_expected">
                      Not as expected
                    </option>
                  </select>
                </div>

                {/* Damage Fields */}
                {returnData.reason === "damaged" && (
                  <>
                    <label>Comments</label>
                    <textarea
                      className="form-control mb-2"
                      name="comments"
                      value={returnData.comments}
                      onChange={handleChange}
                    />

                    <label>Photos</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </>
                )}

                {/* Desired Action */}
                <div className="mb-3">
                  <label>Desired Action</label>
                  <select
                    className="form-control"
                    name="desired_action"
                    value={returnData.desired_action}
                    onChange={handleChange}
                  >
                    <option value="">Select Action</option>
                    <option value="refund">Refund</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>

                {/* Exchange Fields */}
                {returnData.desired_action === "exchange" && (
                  <>
                    <input
                      className="form-control mb-2"
                      name="sku"
                      placeholder="SKU"
                      value={returnData.exchange.sku}
                      onChange={handleExchangeChange}
                    />
                    <input
                      className="form-control mb-2"
                      name="name"
                      placeholder="Product Name"
                      value={returnData.exchange.name}
                      onChange={handleExchangeChange}
                    />
                    <input
                      className="form-control mb-2"
                      name="size"
                      placeholder="Size"
                      value={returnData.exchange.size}
                      onChange={handleExchangeChange}
                    />
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                <button type="submit" className="btn btn-danger">
                  Submit Return
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnByOrder;
